import { Boundaries } from "@planzone/core";
import { id, list, project, event, retrieveEventError } from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";
import { reduceEvent } from "../../common/event";
import { reduceList } from "../../common/list";
import { reduceProject } from "../../common/project";

const initialState: State = [];

function handleRequest(
  state: State,
  eventId: Boundaries["retrieveEvent"]["request"],
): State {
  const stateIndex = state.findIndex(({ id }) => id === eventId);

  return stateIndex !== -1
    ? [
        ...state.slice(0, stateIndex),
        {
          id: eventId,
          request: setRequestStateToPending(),
          event: {
            uuid: id,
            ...event,
            list: { uuid: id, ...list },
            project,
          },
          error: retrieveEventError,
        },
        ...state.slice(stateIndex + 1),
      ]
    : [
        ...state,
        {
          id: eventId,
          request: setRequestStateToPending(),
          event: {
            uuid: id,
            ...event,
            list: { uuid: id, ...list },
            project,
          },
          error: retrieveEventError,
        },
      ];
}

function handleSuccess(
  state: State,
  event: Boundaries["retrieveEvent"]["success"],
): State {
  return state.map((item) =>
    item.id === event.uuid
      ? { ...item, event, request: setRequestStateToSuccess() }
      : item,
  );
}

function handleFailure(
  state: State,
  { eventId, error }: Boundaries["retrieveEvent"]["failure"],
): State {
  return state.map((item) =>
    item.id === eventId
      ? { ...item, request: setRequestStateToFailure() }
      : item,
  );
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((item) => ({
    ...item,
    event: {
      ...item.event,
      ...reduceEvent(item.event, event),
      list: reduceList(item.event.list, event),
      project: reduceProject(item.event.project, event),
    },
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveEventRequested]: handleRequest,
      [events.retrieveEventSucceeded]: handleSuccess,
      [events.retrieveEventFailed]: handleFailure,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
